export interface AssistantSession {
  sessionId: number;
  expiresAt: string;
  clientSessionId?: string;
}

// Chave inclui a publicKey: uma loja não herda a sessão de outra na mesma aba.
const KEY_PREFIX = "indexaai:assistant:session:";
const PENDING_KEY_PREFIX = "indexaai:assistant:pending:";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isActive(
  session: AssistantSession | null,
  now = Date.now(),
): session is AssistantSession {
  return session !== null && Date.parse(session.expiresAt) > now;
}

// sessionStorage pode estar bloqueado (modo privado, iframe): sem ele a sessão fica só na memória.
export function readStoredSession(publicKey: string, now = Date.now()): AssistantSession | null {
  try {
    const raw = sessionStorage.getItem(KEY_PREFIX + publicKey);
    if (!raw) return null;

    const stored = JSON.parse(raw) as Partial<AssistantSession>;
    if (typeof stored.sessionId !== "number" || typeof stored.expiresAt !== "string") return null;

    const session = {
      sessionId: stored.sessionId,
      expiresAt: stored.expiresAt,
      ...(typeof stored.clientSessionId === "string" && UUID_PATTERN.test(stored.clientSessionId)
        ? { clientSessionId: stored.clientSessionId }
        : {}),
    };
    if (isActive(session, now)) return session;
    removeStoredSession(publicKey);
    return null;
  } catch {
    return null;
  }
}

export function writeStoredSession(publicKey: string, session: AssistantSession): void {
  try {
    sessionStorage.setItem(KEY_PREFIX + publicKey, JSON.stringify(session));
  } catch {
    // sem storage a conversa segue na memória desta página
  }
}

export function removeStoredSession(publicKey: string): void {
  try {
    sessionStorage.removeItem(KEY_PREFIX + publicKey);
  } catch {
    // nada a limpar
  }
}

export function readPendingClientSessionId(publicKey: string): string | undefined {
  try {
    const value = sessionStorage.getItem(PENDING_KEY_PREFIX + publicKey);
    return value && UUID_PATTERN.test(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

export function writePendingClientSessionId(publicKey: string, value: string): void {
  try {
    sessionStorage.setItem(PENDING_KEY_PREFIX + publicKey, value);
  } catch {
    // Sem storage a conversa segue na memória desta página.
  }
}

export function removePendingClientSessionId(publicKey: string): void {
  try {
    sessionStorage.removeItem(PENDING_KEY_PREFIX + publicKey);
  } catch {
    // nada a limpar
  }
}
